import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
} from "react";
import { cn } from "@/lib/cn";

type IconComponent = ComponentType<{
  size?: number | string;
  strokeWidth?: number | string;
  "aria-hidden"?: boolean | "true" | "false";
}>;

type AdminIconTone = "neutral" | "accent" | "destructive" | "good";
type AdminIconSize = "sm" | "md";

function iconControlClass(
  tone: AdminIconTone,
  size: AdminIconSize,
  className?: string,
): string {
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded border transition-colors",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
    "disabled:cursor-not-allowed disabled:opacity-40",
    size === "sm" ? "h-7 w-7" : "h-8 w-8",
    tone === "accent"
      ? "border-accent bg-accent text-accent-ink hover:bg-accent-hover"
      : tone === "destructive"
        ? "border-destructive/40 text-destructive-text hover:bg-destructive/10"
        : tone === "good"
          ? "border-good/40 text-good-text hover:bg-good/10"
          : "border-rule text-ink-muted hover:bg-field hover:text-ink",
    className,
  );
}

export type AdminIconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  icon: IconComponent;
  label: string;
  tone?: AdminIconTone;
  size?: AdminIconSize;
  visibleLabel?: boolean;
};

export function AdminIconButton({
  icon: Icon,
  label,
  tone = "neutral",
  size = "sm",
  className,
  visibleLabel = false,
  ...props
}: AdminIconButtonProps) {
  return (
    <button
      type="button"
      aria-label={props["aria-label"] ?? label}
      title={props.title ?? label}
      className={iconControlClass(
        tone,
        size,
        cn(className, visibleLabel && "admin-labelled-action"),
      )}
      {...props}
    >
      <Icon size={size === "sm" ? 14 : 16} strokeWidth={2} aria-hidden="true" />
      {visibleLabel ? <span>{label}</span> : null}
    </button>
  );
}

export type AdminIconLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "children"
> & {
  icon: IconComponent;
  label: string;
  tone?: AdminIconTone;
  size?: AdminIconSize;
};

export function AdminIconLink({
  icon: Icon,
  label,
  tone = "neutral",
  size = "sm",
  className,
  ...props
}: AdminIconLinkProps) {
  return (
    <a
      aria-label={props["aria-label"] ?? label}
      title={props.title ?? label}
      className={iconControlClass(tone, size, className)}
      {...props}
    >
      <Icon size={size === "sm" ? 14 : 16} strokeWidth={2} aria-hidden="true" />
    </a>
  );
}
